import React, { useState } from 'react';
import api from '../services/api';
import { X, CreditCard, Wallet, Smartphone, ShieldCheck, Landmark, Copy, Info } from 'lucide-react';

interface PaymentModalProps {
  paymentData: {
    id: string;
    amount: number;
    type: string;
  };
  onClose: () => void;
  onSuccess: () => void;
}

const PaymentModal: React.FC<PaymentModalProps> = ({ paymentData, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [gateway, setGateway] = useState<'JazzCash' | 'EasyPaisa' | 'BankTransfer'>('JazzCash');
  const [bankDetails, setBankDetails] = useState<any | null>(null);

  const handleProcessPayment = async () => {
    setLoading(true);
    try {
      const response = await api.post('/payments/request', {
        amount: paymentData.amount,
        type: paymentData.type,
        caseId: paymentData.type === 'publication_subscription' ? undefined : (paymentData as any).caseId,
        publicationId: paymentData.type === 'publication_subscription' ? paymentData.id : undefined,
        gateway: gateway
      });

      const { gatewayData } = response.data;

      if (gateway === 'BankTransfer') {
        setBankDetails(gatewayData);
        setLoading(false);
        return;
      }

      // SIMULATION for JazzCash/EasyPaisa
      console.log(`Redirecting to ${gateway} with:`, gatewayData);
      alert(`Simulating redirect to ${gateway} Secure Checkout...\n\nAmount: PKR ${paymentData.amount}\nReference: ${paymentData.id}`);
      
      setTimeout(async () => {
        alert(`${gateway} Payment Successful!\nTransaction ID: MOCK-${Math.random().toString(36).substr(2, 6).toUpperCase()}`);
        onSuccess();
        onClose();
      }, 2000);

    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to initialize payment');
      setLoading(false);
    }
  };

  const handleConfirmBankTransfer = () => {
    alert('Bank Transfer details submitted. Our finance team will verify the transfer within 24 hours.');
    onSuccess();
    onClose();
  };

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 2000, backdropFilter: 'blur(4px)' }}>
      <div style={{ backgroundColor: 'white', padding: '2rem', borderRadius: '12px', maxWidth: '480px', width: '90%', position: 'relative', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)', maxHeight: '90vh', overflowY: 'auto' }}>
        <button onClick={onClose} style={{ position: 'absolute', top: '1rem', right: '1rem', background: 'none', border: 'none', cursor: 'pointer', color: '#718096' }}>
          <X size={24} />
        </button>

        {!bankDetails ? (
          <>
            <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
              <div style={{ backgroundColor: '#f0fff4', width: '60px', height: '60px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1rem' }}>
                <ShieldCheck size={32} color="#38a169" />
              </div>
              <h3 style={{ fontSize: '1.5rem', color: '#1a365d', marginBottom: '0.5rem' }}>Secure Checkout</h3>
              <p style={{ color: '#718096', fontSize: '0.875rem' }}>Complete your payment to activate services.</p>
            </div>

            <div style={{ backgroundColor: '#f8fafc', padding: '1.5rem', borderRadius: '8px', marginBottom: '2rem', border: '1px solid #e2e8f0' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                <span style={{ color: '#4a5568' }}>Service:</span>
                <span style={{ fontWeight: 'bold', textTransform: 'capitalize' }}>{paymentData.type.replace('_', ' ')}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '1.25rem' }}>
                <span style={{ color: '#1a365d', fontWeight: 'bold' }}>Total Amount:</span>
                <span style={{ color: '#2b6cb0', fontWeight: 'bold' }}>PKR {paymentData.amount.toLocaleString()}</span>
              </div>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.75rem', fontWeight: 'bold', fontSize: '0.9rem', color: '#1a365d' }}>Select Payment Method</label>
              <div style={{ display: 'grid', gap: '0.75rem' }}>
                <label style={{ ...methodStyle, borderColor: gateway === 'JazzCash' ? '#2b6cb0' : '#e2e8f0', backgroundColor: gateway === 'JazzCash' ? '#ebf8ff' : 'white' }}>
                  <input type="radio" name="gateway" value="JazzCash" checked={gateway === 'JazzCash'} onChange={() => setGateway('JazzCash')} style={{ display: 'none' }} />
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <div style={{ backgroundColor: '#f56565', color: 'white', width: '80px', textAlign: 'center', padding: '0.4rem', borderRadius: '4px', fontWeight: 'bold', fontSize: '0.7rem' }}>JazzCash</div>
                    <div>
                      <div style={{ fontSize: '0.88rem', fontWeight: 'bold', color: '#1a365d' }}>+92 333 9389948</div>
                      <div style={{ fontSize: '0.72rem', color: '#4a5568' }}>Account Title: NAJEEB ULLAH</div>
                    </div>
                  </div>
                  <Wallet size={18} color={gateway === 'JazzCash' ? '#2b6cb0' : '#cbd5e0'} />
                </label>

                <label style={{ ...methodStyle, borderColor: gateway === 'EasyPaisa' ? '#2b6cb0' : '#e2e8f0', backgroundColor: gateway === 'EasyPaisa' ? '#ebf8ff' : 'white' }}>
                  <input type="radio" name="gateway" value="EasyPaisa" checked={gateway === 'EasyPaisa'} onChange={() => setGateway('EasyPaisa')} style={{ display: 'none' }} />
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <div style={{ backgroundColor: '#38a169', color: 'white', width: '80px', textAlign: 'center', padding: '0.4rem', borderRadius: '4px', fontWeight: 'bold', fontSize: '0.7rem' }}>EasyPaisa</div>
                    <div>
                      <div style={{ fontSize: '0.88rem', fontWeight: 'bold', color: '#1a365d' }}>+92 333 9389948</div>
                      <div style={{ fontSize: '0.72rem', color: '#4a5568' }}>Account Title: NAJEEB ULLAH</div>
                    </div>
                  </div>
                  <Smartphone size={18} color={gateway === 'EasyPaisa' ? '#2b6cb0' : '#cbd5e0'} />
                </label>

                <label style={{ ...methodStyle, borderColor: gateway === 'BankTransfer' ? '#2b6cb0' : '#e2e8f0', backgroundColor: gateway === 'BankTransfer' ? '#ebf8ff' : 'white' }}>
                  <input type="radio" name="gateway" value="BankTransfer" checked={gateway === 'BankTransfer'} onChange={() => setGateway('BankTransfer')} style={{ display: 'none' }} />
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <div style={{ backgroundColor: '#1a365d', color: 'white', width: '80px', textAlign: 'center', padding: '0.4rem', borderRadius: '4px', fontWeight: 'bold', fontSize: '0.7rem' }}>IBAN Banks</div>
                    <div>
                      <div style={{ fontSize: '0.88rem', fontWeight: 'bold', color: '#1a365d' }}>Allied Bank & SCB</div>
                      <div style={{ fontSize: '0.72rem', color: '#4a5568' }}>Account Title: NAJEEB ULLAH</div>
                    </div>
                  </div>
                  <Landmark size={18} color={gateway === 'BankTransfer' ? '#2b6cb0' : '#cbd5e0'} />
                </label>
              </div>
            </div>

            <button 
              onClick={handleProcessPayment}
              disabled={loading}
              style={{ width: '100%', padding: '1rem', backgroundColor: '#1a365d', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.75rem' }}
            >
              {loading ? 'Initializing...' : gateway === 'BankTransfer' ? 'View Bank Details' : `Confirm Payment via ${gateway}`}
            </button>
          </>
        ) : (
          <div>
            <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
              <Landmark size={42} color="#1a365d" style={{ margin: '0 auto 0.5rem' }} />
              <h3 style={{ fontSize: '1.25rem', color: '#1a365d', marginBottom: '0.25rem', fontWeight: '800' }}>Elum Law Banking Details</h3>
              <p style={{ color: '#718096', fontSize: '0.82rem', margin: 0 }}>Transfer PKR {paymentData.amount.toLocaleString()} to any of our designated firm accounts:</p>
            </div>

            <div style={{ display: 'grid', gap: '1rem', marginBottom: '1.5rem' }}>
              
              {/* BANK 1: ALLIED BANK LIMITED */}
              <div style={{ ...bankInfoBox, border: '2px solid #0284c7', backgroundColor: '#f0f9ff' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.25rem' }}>
                  <span style={{ fontSize: '0.75rem', fontWeight: '800', color: '#0369a1', textTransform: 'uppercase' }}>🏦 1. Allied Bank Limited (ABL)</span>
                  <span style={{ fontSize: '0.7rem', backgroundColor: '#0284c7', color: 'white', padding: '0.1rem 0.4rem', borderRadius: '4px', fontWeight: 'bold' }}>Verified</span>
                </div>
                <div style={{ fontSize: '0.85rem', fontWeight: '700', color: '#1e293b', marginBottom: '0.2rem' }}>Account Title: NAJEEB ULLAH</div>
                <div style={{ position: 'relative', backgroundColor: 'white', padding: '0.4rem 0.6rem', borderRadius: '6px', border: '1px solid #bae6fd' }}>
                  <span style={{ fontSize: '0.82rem', fontWeight: '800', color: '#0f172a', fontFamily: 'monospace' }}>PK75ABPA0010118975340013</span>
                  <button onClick={() => { navigator.clipboard.writeText('PK75ABPA0010118975340013'); alert('ABL IBAN Copied!'); }} style={{ position: 'absolute', right: '0.5rem', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#0284c7' }}>
                    <Copy size={15} />
                  </button>
                </div>
              </div>

              {/* BANK 2: STANDARD CHARTERED BANK */}
              <div style={{ ...bankInfoBox, border: '2px solid #16a34a', backgroundColor: '#f0fdf4' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.25rem' }}>
                  <span style={{ fontSize: '0.75rem', fontWeight: '800', color: '#15803d', textTransform: 'uppercase' }}>🏛️ 2. Standard Chartered Bank (SCB)</span>
                  <span style={{ fontSize: '0.7rem', backgroundColor: '#16a34a', color: 'white', padding: '0.1rem 0.4rem', borderRadius: '4px', fontWeight: 'bold' }}>Verified</span>
                </div>
                <div style={{ fontSize: '0.85rem', fontWeight: '700', color: '#1e293b', marginBottom: '0.2rem' }}>Account Title: NAJEEB ULLAH</div>
                <div style={{ position: 'relative', backgroundColor: 'white', padding: '0.4rem 0.6rem', borderRadius: '6px', border: '1px solid #86efac' }}>
                  <span style={{ fontSize: '0.82rem', fontWeight: '800', color: '#0f172a', fontFamily: 'monospace' }}>PK48SCBL0000001025495701</span>
                  <button onClick={() => { navigator.clipboard.writeText('PK48SCBL0000001025495701'); alert('SCB IBAN Copied!'); }} style={{ position: 'absolute', right: '0.5rem', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#16a34a' }}>
                    <Copy size={15} />
                  </button>
                </div>
              </div>

              {/* MOBILE WALLETS SUMMARY */}
              <div style={{ backgroundColor: '#fff7ed', padding: '0.75rem', borderRadius: '8px', border: '1px solid #ffedd5', fontSize: '0.8rem', color: '#9a3412' }}>
                <strong>📱 Easypaisa & Jazzcash Mobile Wallets:</strong><br />
                Number: <strong>+92 333 9389948</strong> | Account Title: <strong>NAJEEB ULLAH</strong>
              </div>

            </div>

            <div style={{ backgroundColor: '#fffaf0', padding: '0.85rem', borderRadius: '8px', border: '1px solid #feebc8', marginBottom: '1.5rem', display: 'flex', gap: '0.65rem' }}>
              <Info size={20} color="#d69e2e" style={{ flexShrink: 0 }} />
              <p style={{ fontSize: '0.78rem', color: '#9c4221', lineHeight: '1.4', margin: 0 }}>
                After payment, send receipt screenshot to WhatsApp <strong>+923339389948</strong> or Email <strong>elumlawassociates@gmail.com</strong> for instant automated activation.
              </p>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <button onClick={() => setBankDetails(null)} style={{ padding: '0.75rem', border: '1px solid #e2e8f0', borderRadius: '8px', background: 'white', cursor: 'pointer', fontWeight: 'bold' }}>Back</button>
              <button onClick={handleConfirmBankTransfer} style={{ padding: '0.75rem', backgroundColor: '#38a169', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' }}>I Have Paid</button>
            </div>
          </div>
        )}
        
        <p style={{ textAlign: 'center', marginTop: '1.5rem', fontSize: '0.7rem', color: '#a0aec0', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
          <ShieldCheck size={12} /> SSL Encrypted Secure Platform
        </p>
      </div>
    </div>
  );
};

const methodStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '0.875rem 1rem',
  border: '2px solid',
  borderRadius: '8px',
  cursor: 'pointer',
  transition: 'all 0.2s'
};

const bankInfoBox: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: '#f8fafc',
  padding: '1rem',
  borderRadius: '8px',
  border: '1px solid #e2e8f0'
};

const bankInfoLabel: React.CSSProperties = {
  fontSize: '0.7rem',
  color: '#718096',
  textTransform: 'uppercase',
  fontWeight: 'bold',
  marginBottom: '0.25rem'
};

const bankInfoValue: React.CSSProperties = {
  fontSize: '1rem',
  fontWeight: 'bold',
  color: '#1a365d'
};

export default PaymentModal;
